import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { addToCartSuccess } from '../../store/slices/cartSlice';
import { addToWishlistSuccess } from '../../store/slices/wishlistSlice';
import { addNotification, openAuthModal } from '../../store/slices/uiSlice';
import {
  CATEGORY_TREE,
  getCategoryBySlug,
  getSubCategoryBySlug
} from '../../data/categoryStructure';
import { dbProducts } from '../../data/db';
import './Shop.css';

const StarRating: React.FC<{ rating: number; size?: number }> = ({ rating, size = 13 }) => (
  <div className="sp-stars">
    {[1, 2, 3, 4, 5].map((s) => (
      <svg key={s} width={size} height={size} viewBox="0 0 24 24"
        fill={s <= rating ? '#F59E0B' : '#E5E7EB'}
        stroke={s <= rating ? '#F59E0B' : '#E5E7EB'} strokeWidth="1">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ))}
  </div>
);

const badgeClass = (badge: string) => {
  if (badge === 'Top Seller') return 'badge--teal';
  if (badge === 'Deal of the Day') return 'badge--green';
  return 'badge--orange';
};

const getBadge = (p: any, idx: number) => {
  if (p.isDeal) return 'Deal of the Day';
  if (idx % 4 === 0) return 'Top Seller';
  if (idx % 4 === 1) return 'Deal of the Day';
  if (idx % 4 === 2) return 'Best of the Day';
  return 'In Demand';
};

const parseNumPrice = (priceStr: any): number => {
  if (typeof priceStr === 'number') return priceStr;
  if (!priceStr) return 0;
  const num = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
  return isNaN(num) ? 0 : num;
};

const getCategoryProducts = (categorySlug?: string, subcategorySlug?: string) => {
  const allProds = dbProducts.products;
  if (!categorySlug) return allProds;

  const catLower = categorySlug.toLowerCase().replace(/-/g, ' ');
  const subLower = subcategorySlug ? subcategorySlug.toLowerCase().replace(/-/g, ' ') : '';

  let filtered = allProds.filter((p: any) => {
    const pCat = (p.category || '').toLowerCase();
    const pName = (p.name || '').toLowerCase();

    const matchesCategory = pCat.includes(catLower) || catLower.includes(pCat) ||
      (catLower.includes('electrical') && pCat.includes('electrical')) ||
      (catLower.includes('security') && pCat.includes('security')) ||
      (catLower.includes('networking') && pCat.includes('it & networking')) ||
      (catLower.includes('lighting') && pCat.includes('lighting')) ||
      (catLower.includes('tools') && (pCat.includes('hardware') || pCat.includes('tools'))) ||
      (catLower.includes('safety') && pCat.includes('safety')) ||
      (catLower.includes('hydraulic') && pCat.includes('hydraulic')) ||
      (catLower.includes('communication') && pCat.includes('communication'));

    if (!matchesCategory) return false;
    if (subLower) {
      return pName.includes(subLower) || pCat.includes(subLower);
    }
    return true;
  });

  if (subLower && filtered.length < 4) {
    filtered = allProds.filter((p: any) => {
      const pCat = (p.category || '').toLowerCase();
      return pCat.includes(catLower) || catLower.includes(pCat) ||
        (catLower.includes('electrical') && pCat.includes('electrical')) ||
        (catLower.includes('security') && pCat.includes('security')) ||
        (catLower.includes('networking') && pCat.includes('it & networking')) ||
        (catLower.includes('lighting') && pCat.includes('lighting')) ||
        (catLower.includes('tools') && (pCat.includes('hardware') || pCat.includes('tools'))) ||
        (catLower.includes('safety') && pCat.includes('safety')) ||
        (catLower.includes('hydraulic') && pCat.includes('hydraulic')) ||
        (catLower.includes('communication') && pCat.includes('communication'));
    });
  }

  return filtered.length > 0 ? filtered : allProds;
};

const Shop: React.FC = () => {
  const { category, subcategory } = useParams<{ category?: string; subcategory?: string }>();
  const topCategory = getCategoryBySlug(category);
  const selectedSubcategory = getSubCategoryBySlug(category, subcategory);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const handleQuickCart = (e: React.MouseEvent, product: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      dispatch(openAuthModal({ message: 'Log in to add items to your cart', pendingAction: 'cart' }));
      return;
    }
    const pNum = product.numPrice || 1999;
    dispatch(addToCartSuccess({
      id: `cart_${Date.now()}_${product.id}`,
      productId: product.id,
      product: {
        id: product.id,
        name: product.name,
        sku: product.id,
        price: Math.round(pNum * 1.3),
        discountedPrice: pNum,
        currency: 'INR',
        image: product.image,
        inStock: true,
        stockQuantity: 50
      },
      quantity: 1,
      addedAt: new Date().toISOString()
    }));
    dispatch(addNotification({ type: 'success', title: 'Added to Cart', message: `"${product.name}" added to cart!` }));
  };

  const handleQuickBuy = (e: React.MouseEvent, product: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/auth/login', { state: { from: { pathname: '/checkout' } } });
      return;
    }
    const pNum = product.numPrice || 1999;
    dispatch(addToCartSuccess({
      id: `cart_${Date.now()}_${product.id}`,
      productId: product.id,
      product: {
        id: product.id,
        name: product.name,
        sku: product.id,
        price: Math.round(pNum * 1.3),
        discountedPrice: pNum,
        currency: 'INR',
        image: product.image,
        inStock: true,
        stockQuantity: 50
      },
      quantity: 1,
      addedAt: new Date().toISOString()
    }));
    navigate('/checkout');
  };

  const handleQuickWishlist = (e: React.MouseEvent, product: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      dispatch(openAuthModal({ message: 'Log in to save items to your wishlist', pendingAction: 'wishlist' }));
      return;
    }
    const pNum = product.numPrice || 1999;
    dispatch(addToWishlistSuccess({
      id: product.id,
      name: product.name,
      description: `${product.name} designed for professional B2B solutions.`,
      sku: product.id,
      category: topCategory?.name || 'Catalog',
      subcategory: 'Industrial',
      brand: 'Generic',
      price: Math.round(pNum * 1.3),
      discountedPrice: pNum,
      currency: 'INR',
      images: [{ id: 'img-1', url: product.image, alt: product.name, isMain: true }],
      specifications: [],
      inStock: true,
      stockQuantity: 50,
      minOrderQuantity: 1,
      rating: product.rating,
      reviewCount: product.reviews,
      tags: [],
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }));
    dispatch(addNotification({ type: 'success', title: 'Added to Wishlist', message: `"${product.name}" added to your wishlist!` }));
  };

  const pageLabel = selectedSubcategory?.name || topCategory?.name || 'Enterprise Catalog';
  const pageDescription = selectedSubcategory
    ? `Explore ${selectedSubcategory.name} products for professional B2B solutions.`
    : topCategory?.description || 'Browse our full enterprise catalog and find solutions across every category.';
  const heroCta = selectedSubcategory ? `Browse ${selectedSubcategory.name}` : topCategory ? `Explore ${topCategory.name}` : 'Discover products';

  const sidebarItems = topCategory?.children || CATEGORY_TREE.map((top) => ({
    name: top.name,
    count: top.productCount || 0,
    path: top.path
  }));

  const rawProducts = useMemo(() => {
    const list = getCategoryProducts(category, subcategory);
    return list.map((p: any, idx: number) => ({
      id: p.id,
      name: p.name,
      rating: parseFloat(p.rating || '4.0'),
      reviews: p.reviewsCount || Math.floor(Math.random() * 80) + 12,
      price: p.price,
      oldPrice: p.oldPrice,
      discount: p.discount,
      image: p.image || '/assets/images/camera.png',
      badge: getBadge(p, idx),
      inStock: p.inStock !== false,
      numPrice: parseNumPrice(p.price)
    }));
  }, [category, subcategory]);

  const [priceFilters, setPriceFilters] = useState<string[]>([]);
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [badgeFilters, setBadgeFilters] = useState<string[]>([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('Popularity');

  const toggleArr = (arr: string[], val: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);
  };

  const filteredProducts = useMemo(() => {
    let result = [...rawProducts];

    if (inStockOnly) {
      result = result.filter((p) => p.inStock);
    }

    if (ratingFilter !== null) {
      result = result.filter((p) => p.rating >= ratingFilter);
    }

    if (badgeFilters.length > 0) {
      result = result.filter((p) => badgeFilters.includes(p.badge));
    }

    if (priceFilters.length > 0) {
      result = result.filter((p) => {
        const pr = p.numPrice;
        return priceFilters.some((range) => {
          if (range === '₹0-₹500') return pr <= 500;
          if (range === '₹500-₹1,500') return pr > 500 && pr <= 1500;
          if (range === '₹1,500-₹5,000') return pr > 1500 && pr <= 5000;
          if (range === '₹5,000+') return pr > 5000;
          return true;
        });
      });
    }

    if (sortBy === 'Price: Low to High') {
      result.sort((a, b) => a.numPrice - b.numPrice);
    } else if (sortBy === 'Price: High to Low') {
      result.sort((a, b) => b.numPrice - a.numPrice);
    } else if (sortBy === 'Newest First') {
      result.reverse();
    }

    return result;
  }, [rawProducts, inStockOnly, ratingFilter, badgeFilters, priceFilters, sortBy]);

  const featuredCategories = useMemo(() => {
    const base = topCategory?.featuredSubcategories || CATEGORY_TREE.slice(0, 6).map((top) => ({ name: top.name, path: top.path, icon: top.icon }));
    const usedImages = new Set<string>();

    return base.map((cat, idx) => {
      const keywords = cat.name.toLowerCase().split(/[\s/&,-]+/);
      const match = dbProducts.products.find((p: any) => {
        const pName = (p.name || '').toLowerCase();
        const pCat = (p.category || '').toLowerCase();
        return keywords.some((k) => k.length > 2 && (pName.includes(k) || pCat.includes(k)));
      });

      let chosenImg = match?.image;

      if (!chosenImg || usedImages.has(chosenImg)) {
        const fallbackProd = rawProducts[idx % rawProducts.length];
        chosenImg = fallbackProd?.image || '/assets/images/camera.png';
      }

      usedImages.add(chosenImg);

      return {
        ...cat,
        image: chosenImg
      };
    });
  }, [topCategory, rawProducts]);

  const heroImages = useMemo(() => {
    const images = Array.from(new Set(rawProducts.map((p) => p.image).filter(Boolean)));
    return [
      images[0] || '/assets/images/camera.png',
      images[1] || images[0] || '/assets/images/camera.png',
      images[2] || images[0] || '/assets/images/camera.png'
    ];
  }, [rawProducts]);

  return (
    <div className="sp-page">
      <div className="sp-container">
        <nav className="sp-breadcrumb">
          <Link to="/">Home</Link>
          <span>&gt;</span>
          <Link to="/shop">Shop</Link>
          {topCategory && (
            <>
              <span>&gt;</span>
              <Link to={topCategory.path}>{topCategory.name}</Link>
            </>
          )}
          {selectedSubcategory && (
            <>
              <span>&gt;</span>
              <span className="sp-breadcrumb__current">{selectedSubcategory.name}</span>
            </>
          )}
        </nav>

        <div className="sp-layout">
          <aside className="sp-sidebar">
            <div className="sp-filter">
              <h4 className="sp-filter__title">Browse categories</h4>
              {sidebarItems.map((item) => (
                <label key={item.name} className="sp-filter__check">
                  <input type="checkbox" readOnly checked={selectedSubcategory?.name === item.name || topCategory?.name === item.name} />
                  <span>{item.name} ({item.count})</span>
                </label>
              ))}
            </div>

            <div className="sp-filter">
              <h4 className="sp-filter__title">Price Range</h4>
              {['₹0-₹500', '₹500-₹1,500', '₹1,500-₹5,000', '₹5,000+'].map((price) => (
                <label key={price} className="sp-filter__check">
                  <input type="checkbox" onChange={() => toggleArr(priceFilters, price, setPriceFilters)} checked={priceFilters.includes(price)} />
                  <span>{price}</span>
                </label>
              ))}
            </div>

            <div className="sp-filter">
              <h4 className="sp-filter__title">Availability</h4>
              <label className="sp-filter__check">
                <input type="checkbox" checked={inStockOnly} onChange={() => setInStockOnly(!inStockOnly)} />
                <span>In stock only</span>
              </label>
            </div>

            <div className="sp-filter">
              <h4 className="sp-filter__title">Ratings</h4>
              {[4, 3, 2].map((rating) => (
                <label key={rating} className="sp-filter__check sp-filter__check--rating">
                  <input type="radio" name="rating" onChange={() => setRatingFilter(ratingFilter === rating ? null : rating)} checked={ratingFilter === rating} />
                  <StarRating rating={rating} size={12} />
                  <span className="sp-filter__rating-label">{rating}+ stars</span>
                </label>
              ))}
            </div>

            <div className="sp-filter">
              <h4 className="sp-filter__title">Badges</h4>
              {['Top Seller', 'Deal of the Day', 'Best of the Day', 'In Demand'].map((label) => (
                <label key={label} className="sp-filter__check">
                  <input type="checkbox" onChange={() => toggleArr(badgeFilters, label, setBadgeFilters)} checked={badgeFilters.includes(label)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </aside>

          <main className="sp-main">
            <div className="sp-hero">
              <div className="sp-hero__text">
                <p className="sp-hero__sub">{pageDescription}</p>
                <h2 className="sp-hero__title">{pageLabel}</h2>
                <Link to={topCategory?.path || '/shop'} className="sp-hero__btn">{heroCta}</Link>
              </div>
              <div className="sp-hero__imgs">
                <img src={heroImages[0]} alt="Product" className="sp-hero__cam sp-hero__cam--1" />
                <img src={heroImages[1]} alt="Product" className="sp-hero__cam sp-hero__cam--2" />
                <img src={heroImages[2]} alt="Product" className="sp-hero__cam sp-hero__cam--3" />
              </div>
              <div className="sp-hero__tags">
                <span>Enterprise</span><span>Bulk Pricing</span><span>Trusted Brands</span>
              </div>
            </div>

            <div className="sp-feat-cats">
              <h3 className="sp-section-title">Featured categories</h3>
              <div className="sp-feat-cats__track">
                {featuredCategories.map((cat) => (
                  <Link key={cat.path} to={cat.path} className="sp-feat-cat-card">
                    <div className="sp-feat-cat-card__img">
                      <img src={cat.image} alt={cat.name} />
                    </div>
                    <div className="sp-feat-cat-card__name">{cat.name}</div>
                    <button className="sp-feat-cat-card__btn">View Category →</button>
                  </Link>
                ))}
              </div>
            </div>

            <div className="sp-feat-prods">
              <h3 className="sp-section-title">Featured Products</h3>
              <div className="sp-feat-prods__grid">
                {rawProducts.slice(0, 4).map((product) => (
                  <Link to={`/product/${encodeURIComponent(product.id)}`} key={product.id} className="sp-prod-card">
                    <div className={`sp-prod-card__badge ${badgeClass(product.badge)}`}>{product.badge}</div>
                    <div className="sp-prod-card__img">
                      <img src={product.image} alt={product.name} loading="lazy" />
                    </div>
                    <div className="sp-prod-card__info">
                      <div>
                        <div className="sp-prod-card__rating-row">
                          <StarRating rating={product.rating} />
                          <span className="sp-prod-card__reviews">{product.reviews} Reviews</span>
                        </div>
                        <p className="sp-prod-card__name" title={product.name}>{product.name}</p>
                      </div>
                      <div className="sp-prod-card__price-row">
                        <span className="sp-prod-card__price">{product.price}</span>
                        {product.oldPrice && <span className="sp-prod-card__old-price">{product.oldPrice}</span>}
                        {product.discount && <span className="sp-prod-card__discount">{product.discount}</span>}
                      </div>
                      <div className="sp-prod-card__actions">
                        <button className="sp-prod-card__btn-wishlist" onClick={(e) => handleQuickWishlist(e, product)} title="Add to Wishlist">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        </button>
                        <button className="sp-prod-card__btn-cart" onClick={(e) => handleQuickCart(e, product)}>
                          + Cart
                        </button>
                        <button className="sp-prod-card__btn-buy" onClick={(e) => handleQuickBuy(e, product)}>
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="sp-shop">
              <div className="sp-shop__toolbar">
                <div className="sp-shop__count">
                  Showing <strong>{filteredProducts.length}</strong> products
                </div>
                <div className="sp-shop__sort">
                  <span className="sp-shop__sort-label">Sort by:</span>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sp-shop__sort-select">
                    <option>Popularity</option>
                    <option>Price: Low to High</option>
                    <option>Price: High to Low</option>
                    <option>Newest First</option>
                  </select>
                </div>
              </div>

              <h3 className="sp-section-title">Shop {pageLabel}</h3>
              <div className="sp-prod-grid">
                {filteredProducts.map((product) => (
                  <Link to={`/product/${encodeURIComponent(product.id)}`} key={product.id} className="sp-prod-card">
                    <div className={`sp-prod-card__badge ${badgeClass(product.badge)}`}>{product.badge}</div>
                    <div className="sp-prod-card__img">
                      <img src={product.image} alt={product.name} loading="lazy" />
                    </div>
                    <div className="sp-prod-card__info">
                      <div>
                        <div className="sp-prod-card__rating-row">
                          <StarRating rating={product.rating} />
                          <span className="sp-prod-card__reviews">{product.reviews} Reviews</span>
                        </div>
                        <p className="sp-prod-card__name" title={product.name}>{product.name}</p>
                      </div>
                      <div className="sp-prod-card__price-row">
                        <span className="sp-prod-card__price">{product.price}</span>
                        {product.oldPrice && <span className="sp-prod-card__old-price">{product.oldPrice}</span>}
                        {product.discount && <span className="sp-prod-card__discount">{product.discount}</span>}
                      </div>
                      <div className="sp-prod-card__actions">
                        <button className="sp-prod-card__btn-wishlist" onClick={(e) => handleQuickWishlist(e, product)} title="Add to Wishlist">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        </button>
                        <button className="sp-prod-card__btn-cart" onClick={(e) => handleQuickCart(e, product)}>
                          + Cart
                        </button>
                        <button className="sp-prod-card__btn-buy" onClick={(e) => handleQuickBuy(e, product)}>
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Shop;
